/**
 * notifications.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Admin notifications — compose & send to one user or all users.
 * View, filter, and delete notification history.
 * Route: /admin/notifications
 *
 * Table: notifications
 *   id, user_id, category, title, description, icon_class, icon_bg_css,
 *   icon_color_css, dot_color_css, is_read, is_dismissed, tags, action_url,
 *   created_at
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import supabase from "../supabase";
import {
  // sidebar
  LayoutDashboard, Wallet, Receipt, Copy, UserCheck,
  HeadphonesIcon, Bell, CandlestickChart, BarChart2, ShoppingBag,
  ChevronRight, LogOut, Users,CreditCard, MapPin,
  // page
  Search, RefreshCw, Plus, X, AlertCircle,
  CheckCircle2, XCircle, Clock, Trash2,
  ChevronDown, ChevronUp, Send, User,
  Tag, Link, Filter, Eye, EyeOff, Menu,
} from "lucide-react";

const PER_PAGE = 15;

// ─── Sidebar nav ─────────────────────────────────────────────────────────────
const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [{ to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true }],
  },
  {
    label: "Users & Finance",
    items: [
      { to: "/admin/users",        label: "Users",        icon: Users   },
      { to: "/admin/wallets",      label: "Wallets",      icon: Wallet  },
      { to: "/admin/transactions", label: "Transactions", icon: Receipt },
    ],
  },
  {
    label: "Trading",
    items: [
      { to: "/admin/copy-trades",  label: "Copy trades",   icon: Copy             },
      { to: "/admin/traders",      label: "Hired traders", icon: UserCheck        },
      { to: "/admin/trading",      label: "Open trades",   icon: CandlestickChart },
      { to: "/admin/stock-trades", label: "Stock trades",  icon: BarChart2        },
      { to: "/admin/marketplace",  label: "Marketplace",   icon: ShoppingBag      },
    ],
  },
  {
    label: "Communication",
    items: [
      { to: "/admin/support",       label: "Support",       icon: HeadphonesIcon },
      { to: "/admin/notifications", label: "Notifications", icon: Bell           },
    ],
  },
   {
    label: "Settings",
    items: [
      { to: "/admin/subscriptions",      label: "Subscriptions",      icon: CreditCard },
      { to: "/admin/payment-addresses",  label: "Payment Addresses",  icon: MapPin     },
      { to: "/admin/market-subscriptions",  label: "Market Subscriptions",  icon: MapPin     },
    ],
  },
];

// ─── Categories ───────────────────────────────────────────────────────────────
const CATEGORIES = [
  { value: "general",     label: "General"     },
  { value: "trade",       label: "Trade"       },
  { value: "wallet",      label: "Wallet"      },
  { value: "security",    label: "Security"    },
  { value: "system",      label: "System"      },
  { value: "promotion",   label: "Promotion"   },
];

// ─── Styles ───────────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg-base:      #06060f;
    --bg-surface:   #0a0a16;
    --bg-elevated:  #0e0e1c;
    --bg-card:      #111122;
    --bg-hover:     #14142a;
    --border:       rgba(255,255,255,0.055);
    --border-hi:    rgba(255,255,255,0.11);
    --accent:       #6366f1;
    --accent-light: #818cf8;
    --accent-dim:   rgba(99,102,241,0.13);
    --accent-glow:  rgba(99,102,241,0.22);
    --green:        #34d399;
    --green-dim:    rgba(52,211,153,0.13);
    --amber:        #fbbf24;
    --amber-dim:    rgba(251,191,36,0.13);
    --rose:         #fb7185;
    --rose-dim:     rgba(251,113,133,0.13);
    --cyan:         #22d3ee;
    --cyan-dim:     rgba(34,211,238,0.13);
    --purple:       #a78bfa;
    --purple-dim:   rgba(167,139,250,0.13);
    --text-primary:   #eeeeff;
    --text-secondary: #7878a0;
    --text-muted:     #3e3e58;
    --font-sans: 'Inter', system-ui, sans-serif;
    --font-mono: 'JetBrains Mono', monospace;
    --sidebar-w: 232px;
    --topbar-h:  60px;
    --radius-sm: 8px;
    --radius-md: 14px;
    --radius-lg: 18px;
  }

  html, body, #root { height: 100%; background: var(--bg-base); font-family: var(--font-sans); color-scheme: dark; }

  /* Shell */
  .nt-shell { display: flex; height: 100vh; overflow: hidden; background: var(--bg-base); position: relative; }
  .nt-shell::before {
    content:''; position:fixed; inset:0; pointer-events:none; z-index:0;
    background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
    opacity:0.018;
  }

  /* Sidebar */
  .ad-sidebar {
    width: var(--sidebar-w); flex-shrink: 0; height: 100%; min-height: 0; display: flex; flex-direction: column;
    background: var(--bg-surface); border-right: 1px solid var(--border); position: relative; z-index: 10;
  }
  .ad-sidebar::after {
    content:''; position:absolute; top:0; right:0; bottom:0; width:1px;
    background: linear-gradient(to bottom, transparent 0%, var(--accent-glow) 35%, rgba(99,102,241,0.08) 70%, transparent 100%);
    pointer-events:none;
  }
  .ad-logo { display:flex; align-items:center; gap:11px; padding:0 18px; height:var(--topbar-h); border-bottom:1px solid var(--border); flex-shrink:0; }
  .ad-logo-icon {
    width:32px; height:32px; border-radius:10px;
    background:linear-gradient(135deg,#6366f1 0%,#8b5cf6 50%,#a78bfa 100%);
    display:flex; align-items:center; justify-content:center; flex-shrink:0;
    box-shadow:0 0 0 1px rgba(99,102,241,0.4),0 4px 16px rgba(99,102,241,0.35),0 0 32px rgba(99,102,241,0.15);
  }
  .ad-logo-text strong { font-size:13.5px; font-weight:800; letter-spacing:-0.4px; background:linear-gradient(to right,#eeeeff,#a5b4fc); -webkit-background-clip:text; -webkit-text-fill-color:transparent; }
  .ad-logo-text span   { font-size:9.5px; color:var(--text-muted); display:block; margin-top:3px; letter-spacing:0.8px; text-transform:uppercase; font-weight:500; }
  .ad-nav { flex:1; min-height:0; overflow-y:auto; padding:18px 10px; display:flex; flex-direction:column; gap:22px; }
  .ad-nav::-webkit-scrollbar { width:0; }
  .ad-nav-section-label { font-size:9px; font-weight:700; letter-spacing:1.5px; text-transform:uppercase; color:var(--text-muted); padding:0 10px; margin-bottom:5px; }
  .ad-nav-items { display:flex; flex-direction:column; gap:1px; }
  .ad-nav-link { display:flex; align-items:center; gap:9px; padding:8px 10px; border-radius:var(--radius-sm); font-size:12.5px; font-weight:500; color:var(--text-secondary); text-decoration:none; transition:all 0.18s ease; position:relative; }
  .ad-nav-link:hover { background:var(--bg-hover); color:var(--text-primary); }
  .ad-nav-link.active { background:linear-gradient(135deg,rgba(99,102,241,0.18) 0%,rgba(139,92,246,0.10) 100%); color:var(--accent-light); font-weight:600; box-shadow:inset 0 0 0 1px rgba(99,102,241,0.15); }
  .ad-nav-link.active::before { content:''; position:absolute; left:0; top:18%; bottom:18%; width:2.5px; border-radius:99px; background:linear-gradient(to bottom,var(--accent),var(--purple)); box-shadow:0 0 10px var(--accent-glow),0 0 20px rgba(99,102,241,0.2); }
  .ad-nav-link svg { flex-shrink:0; opacity:0.65; transition:opacity 0.15s; }
  .ad-nav-link:hover svg, .ad-nav-link.active svg { opacity:1; }
  .ad-nav-chevron { margin-left:auto; opacity:0.35; }
  .ad-user-card { flex-shrink:0; border-top:1px solid var(--border); padding:12px 10px; background:linear-gradient(to top,rgba(99,102,241,0.04) 0%,transparent 100%); }
  .ad-user-inner { display:flex; align-items:center; gap:10px; padding:9px 10px; border-radius:var(--radius-sm); transition:background 0.15s; }
  .ad-user-inner:hover { background:var(--bg-hover); }
  .ad-user-avatar { width:32px; height:32px; border-radius:50%; background:linear-gradient(135deg,var(--accent-dim),rgba(139,92,246,0.2)); border:1.5px solid rgba(99,102,241,0.35); display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; color:var(--accent-light); flex-shrink:0; }
  .ad-user-info { flex:1; min-width:0; }
  .ad-user-info strong { font-size:12px; font-weight:600; color:var(--text-primary); display:block; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .ad-user-info span   { font-size:10px; color:var(--text-muted); display:block; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-top:1px; }
  .ad-logout-btn { width:28px; height:28px; border-radius:7px; background:none; border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; color:var(--text-muted); transition:all 0.15s; }
  .ad-logout-btn:hover { background:var(--rose-dim); color:var(--rose); }

  /* Page */
  .nt-page { flex:1; display:flex; flex-direction:column; overflow:hidden; position:relative; z-index:1; min-height:0; min-width:0; }

  /* Topbar */
  .nt-topbar { flex-shrink:0; height:var(--topbar-h); background:rgba(10,10,22,0.85); backdrop-filter:blur(12px); border-bottom:1px solid var(--border); display:flex; align-items:center; justify-content:space-between; padding:0 28px; position:relative; }
  .nt-topbar::after { content:''; position:absolute; bottom:-1px; left:0; right:0; height:1px; background:linear-gradient(to right,transparent 0%,var(--accent-glow) 50%,transparent 100%); }
  .nt-topbar-left  { display:flex; align-items:center; gap:14px; }
  .nt-topbar-right { display:flex; align-items:center; gap:8px; }
  .nt-topbar-badge { display:flex; align-items:center; gap:6px; padding:3px 10px; border-radius:99px; background:var(--accent-dim); border:1px solid rgba(99,102,241,0.2); font-size:10px; font-weight:700; color:var(--accent-light); letter-spacing:0.5px; text-transform:uppercase; }
  .nt-topbar-title { font-size:15px; font-weight:700; color:var(--text-primary); letter-spacing:-0.4px; }
  .nt-topbar-sub   { font-size:11px; color:var(--text-muted); margin-top:1px; }

  /* Buttons */
  .nt-btn { display:flex; align-items:center; gap:6px; padding:6px 14px; border-radius:var(--radius-sm); font-size:11.5px; font-weight:500; cursor:pointer; transition:all 0.18s; font-family:var(--font-sans); border:1px solid var(--border-hi); background:var(--bg-card); color:var(--text-secondary); }
  .nt-btn:hover { background:var(--accent-dim); color:var(--accent-light); border-color:rgba(99,102,241,0.3); }
  .nt-btn-primary { display:flex; align-items:center; gap:6px; padding:6px 16px; border-radius:var(--radius-sm); font-size:12px; font-weight:600; cursor:pointer; transition:all 0.18s; font-family:var(--font-sans); border:none; background:var(--accent); color:#fff; }
  .nt-btn-primary:hover:not(:disabled) { background:#4f52e0; box-shadow:0 0 20px rgba(99,102,241,0.35); }
  .nt-btn-primary:disabled { opacity:0.4; cursor:not-allowed; }

  /* Main */
  .nt-main { flex:1; overflow-y:auto; overflow-x:hidden; min-height:0; padding:26px 28px; display:flex; flex-direction:column; gap:20px; }
  .nt-main::-webkit-scrollbar { width:4px; }
  .nt-main::-webkit-scrollbar-thumb { background:rgba(99,102,241,0.25); border-radius:99px; }

  /* Stat grid */
  .nt-stat-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:14px; }
  .nt-stat { background:var(--bg-card); border:1px solid var(--border); border-radius:var(--radius-md); padding:18px 20px; display:flex; flex-direction:column; gap:12px; position:relative; overflow:hidden; transition:border-color 0.22s,box-shadow 0.22s,transform 0.18s; }
  .nt-stat::before { content:''; position:absolute; inset:0; background:radial-gradient(ellipse at 110% -10%,var(--sg,transparent) 0%,transparent 60%); pointer-events:none; }
  .nt-stat::after  { content:''; position:absolute; bottom:0; left:0; right:0; height:1px; background:linear-gradient(to right,transparent 20%,var(--sb,transparent) 50%,transparent 80%); pointer-events:none; }
  .nt-stat:hover { border-color:rgba(255,255,255,0.1); box-shadow:0 4px 24px rgba(0,0,0,0.4); transform:translateY(-1px); }
  .nt-stat-top   { display:flex; align-items:flex-start; justify-content:space-between; }
  .nt-stat-label { font-size:9.5px; font-weight:700; letter-spacing:1.1px; text-transform:uppercase; color:var(--text-muted); }
  .nt-stat-icon  { width:32px; height:32px; border-radius:9px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .nt-stat-value { font-size:26px; font-weight:700; color:var(--text-primary); letter-spacing:-1.5px; font-family:var(--font-mono); line-height:1; }
  .nt-stat-sub   { font-size:10.5px; color:var(--text-muted); margin-top:4px; }

  /* Two-col layout */
  .nt-layout { display:grid; grid-template-columns:420px 1fr; gap:20px; align-items:start; }

  /* Panel */
  .nt-panel { background:var(--bg-card); border:1px solid var(--border); border-radius:var(--radius-md); overflow:hidden; }
  .nt-panel-head { padding:16px 20px; border-bottom:1px solid var(--border); }
  .nt-panel-title { font-size:13px; font-weight:700; color:var(--text-primary); }
  .nt-panel-sub   { font-size:11px; color:var(--text-muted); margin-top:3px; }

  /* Tabs */
  .nt-tabs { display:flex; gap:2px; padding:4px; background:var(--bg-elevated); border:1px solid var(--border); border-radius:var(--radius-md); width:fit-content; }
  .nt-tab { display:flex; align-items:center; gap:7px; padding:7px 16px; border-radius:10px; font-size:12.5px; font-weight:500; cursor:pointer; transition:all 0.18s; background:none; border:none; color:var(--text-muted); font-family:var(--font-sans); }
  .nt-tab:hover  { color:var(--text-secondary); }
  .nt-tab.active { background:linear-gradient(135deg,rgba(99,102,241,0.2),rgba(139,92,246,0.12)); color:var(--accent-light); font-weight:600; box-shadow:inset 0 0 0 1px rgba(99,102,241,0.2); }
  .nt-tab-badge  { padding:1px 7px; border-radius:99px; font-size:10px; font-weight:700; font-family:var(--font-mono); background:rgba(255,255,255,0.07); color:var(--text-muted); }
  .nt-tab-badge.unread { background:var(--accent); color:#fff; }

  /* Form */
  .nt-form { display:flex; flex-direction:column; gap:16px; padding:20px; }
  .nt-label { font-size:11px; font-weight:600; color:var(--text-secondary); margin-bottom:6px; display:block; }
  .nt-input { width:100%; padding:9px 12px; background:var(--bg-surface); border:1px solid var(--border); border-radius:var(--radius-sm); font-size:13px; color:var(--text-primary); font-family:var(--font-sans); outline:none; transition:border-color 0.18s; }
  .nt-input::placeholder { color:var(--text-muted); }
  .nt-input:focus { border-color:rgba(99,102,241,0.45); box-shadow:0 0 0 3px rgba(99,102,241,0.08); }
  .nt-textarea { width:100%; padding:9px 12px; background:var(--bg-surface); border:1px solid var(--border); border-radius:var(--radius-sm); font-size:13px; color:var(--text-primary); font-family:var(--font-sans); outline:none; transition:border-color 0.18s; resize:vertical; min-height:90px; }
  .nt-textarea::placeholder { color:var(--text-muted); }
  .nt-textarea:focus { border-color:rgba(99,102,241,0.45); box-shadow:0 0 0 3px rgba(99,102,241,0.08); }
  .nt-select { width:100%; padding:9px 12px; background:var(--bg-surface); border:1px solid var(--border); border-radius:var(--radius-sm); font-size:13px; color:var(--text-primary); font-family:var(--font-sans); outline:none; transition:border-color 0.18s; cursor:pointer; }
  .nt-select:focus { border-color:rgba(99,102,241,0.45); }
  .nt-row { display:grid; grid-template-columns:1fr 1fr; gap:12px; }

  /* Target toggle */
  .nt-target-row { display:flex; gap:8px; }
  .nt-target-btn { flex:1; display:flex; align-items:center; justify-content:center; gap:8px; padding:10px 12px; border-radius:var(--radius-sm); font-size:12.5px; font-weight:500; cursor:pointer; transition:all 0.18s; font-family:var(--font-sans); border:1px solid var(--border); background:none; color:var(--text-muted); }
  .nt-target-btn:hover { background:var(--bg-hover); color:var(--text-primary); }
  .nt-target-btn.active { background:var(--accent-dim); color:var(--accent-light); border-color:rgba(99,102,241,0.3); font-weight:600; }

  /* Category pills */
  .nt-cat-pills { display:flex; flex-wrap:wrap; gap:6px; }
  .nt-cat-pill { padding:4px 12px; border-radius:99px; font-size:11.5px; font-weight:500; cursor:pointer; transition:all 0.15s; border:1px solid var(--border); background:none; color:var(--text-muted); font-family:var(--font-sans); }
  .nt-cat-pill:hover  { background:var(--bg-hover); color:var(--text-primary); }
  .nt-cat-pill.active { background:var(--accent-dim); color:var(--accent-light); border-color:rgba(99,102,241,0.3); font-weight:600; }

  /* User picker */
  .nt-user-list { max-height:160px; overflow-y:auto; background:var(--bg-surface); border:1px solid var(--border); border-radius:var(--radius-sm); margin-top:8px; }
  .nt-user-list::-webkit-scrollbar { width:3px; }
  .nt-user-list::-webkit-scrollbar-thumb { background:rgba(99,102,241,0.2); border-radius:99px; }
  .nt-user-item { display:flex; align-items:center; gap:10px; padding:8px 12px; cursor:pointer; transition:background 0.12s; border-bottom:1px solid rgba(255,255,255,0.035); }
  .nt-user-item:last-child { border-bottom:none; }
  .nt-user-item:hover   { background:var(--bg-hover); }
  .nt-user-item.selected { background:var(--accent-dim); }
  .nt-avatar { width:28px; height:28px; border-radius:50%; flex-shrink:0; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; border:1.5px solid rgba(99,102,241,0.2); }
  .nt-avatar-indigo { background:rgba(99,102,241,0.15); color:#818cf8; }
  .nt-avatar-green  { background:rgba(52,211,153,0.15);  color:#34d399; }
  .nt-avatar-amber  { background:rgba(251,191,36,0.15);  color:#fbbf24; }
  .nt-avatar-rose   { background:rgba(251,113,133,0.15); color:#fb7185; }
  .nt-avatar-purple { background:rgba(167,139,250,0.15); color:#a78bfa; }

  /* Tags input */
  .nt-tags-wrap { display:flex; flex-wrap:wrap; gap:6px; padding:8px 10px; background:var(--bg-surface); border:1px solid var(--border); border-radius:var(--radius-sm); min-height:42px; cursor:text; transition:border-color 0.18s; }
  .nt-tags-wrap:focus-within { border-color:rgba(99,102,241,0.45); box-shadow:0 0 0 3px rgba(99,102,241,0.08); }
  .nt-tag { display:inline-flex; align-items:center; gap:5px; padding:2px 8px; border-radius:99px; font-size:11px; font-weight:600; background:var(--accent-dim); color:var(--accent-light); border:1px solid rgba(99,102,241,0.25); }
  .nt-tag-remove { background:none; border:none; cursor:pointer; color:var(--accent-light); padding:0; display:flex; opacity:0.6; transition:opacity 0.15s; }
  .nt-tag-remove:hover { opacity:1; }
  .nt-tag-input { background:none; border:none; outline:none; font-size:12px; color:var(--text-primary); font-family:var(--font-sans); flex:1; min-width:80px; }
  .nt-tag-input::placeholder { color:var(--text-muted); }

  /* Search wrap */
  .nt-search-wrap { position:relative; }
  .nt-search-icon { position:absolute; left:10px; top:50%; transform:translateY(-50%); color:var(--text-muted); pointer-events:none; }
  .nt-search { padding:6px 12px 6px 30px; background:var(--bg-surface); border:1px solid var(--border); border-radius:var(--radius-sm); font-size:12px; color:var(--text-primary); font-family:var(--font-sans); width:200px; outline:none; transition:border-color 0.18s; }
  .nt-search::placeholder { color:var(--text-muted); }
  .nt-search:focus { border-color:rgba(99,102,241,0.4); }

  .nt-filter-select { padding:5px 10px; background:var(--bg-surface); border:1px solid var(--border); border-radius:var(--radius-sm); font-size:11.5px; color:var(--text-primary); font-family:var(--font-sans); outline:none; cursor:pointer; }
  .nt-filter-select:focus { border-color:rgba(99,102,241,0.4); }

  /* Toolbar */
  .nt-toolbar { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:14px 20px; border-bottom:1px solid var(--border); flex-wrap:wrap; }
  .nt-toolbar-left  { display:flex; align-items:center; gap:10px; }
  .nt-toolbar-right { display:flex; align-items:center; gap:8px; }
  .nt-count-pill { padding:2px 8px; border-radius:99px; border:1px solid var(--border-hi); font-size:10px; color:var(--text-muted); font-family:var(--font-mono); }

  /* History rows */
  .nt-row-item { display:flex; align-items:flex-start; gap:14px; padding:14px 20px; border-bottom:1px solid rgba(255,255,255,0.035); transition:background 0.12s; position:relative; }
  .nt-row-item:last-child { border-bottom:none; }
  .nt-row-item:hover { background:rgba(99,102,241,0.03); }
  .nt-row-item:hover .nt-delete-btn { opacity:1; }
  .nt-notif-icon { width:34px; height:34px; border-radius:9px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .nt-row-body    { flex:1; min-width:0; }
  .nt-row-top     { display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-bottom:2px; }
  .nt-row-title   { font-size:13px; font-weight:600; color:var(--text-primary); }
  .nt-row-desc    { font-size:12px; color:var(--text-secondary); line-height:1.5; margin-bottom:5px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:500px; }
  .nt-row-meta    { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
  .nt-row-date    { font-size:10.5px; color:var(--text-muted); font-family:var(--font-mono); }
  .nt-row-to      { font-size:10.5px; color:var(--text-muted); }
  .nt-row-to strong { color:var(--accent-light); }
  .nt-unread-dot  { width:6px; height:6px; border-radius:50%; background:var(--accent); flex-shrink:0; }

  /* Category chip */
  .nt-chip { display:inline-flex; align-items:center; gap:4px; padding:2px 8px; border-radius:99px; font-size:10px; font-weight:700; letter-spacing:0.3px; text-transform:capitalize; border:1px solid; }
  .nt-chip-general   { background:rgba(99,102,241,0.1);  color:#818cf8; border-color:rgba(99,102,241,0.25);  }
  .nt-chip-trade     { background:rgba(52,211,153,0.1);  color:#34d399; border-color:rgba(52,211,153,0.25);  }
  .nt-chip-wallet    { background:rgba(251,191,36,0.1);  color:#fbbf24; border-color:rgba(251,191,36,0.25);  }
  .nt-chip-security  { background:rgba(251,113,133,0.1); color:#fb7185; border-color:rgba(251,113,133,0.25); }
  .nt-chip-system    { background:rgba(34,211,238,0.1);  color:#22d3ee; border-color:rgba(34,211,238,0.25);  }
  .nt-chip-promotion { background:rgba(167,139,250,0.1); color:#a78bfa; border-color:rgba(167,139,250,0.25); }

  /* Delete btn */
  .nt-delete-btn { width:28px; height:28px; border-radius:7px; display:flex; align-items:center; justify-content:center; background:none; border:1px solid var(--border); color:var(--text-muted); cursor:pointer; transition:all 0.15s; flex-shrink:0; opacity:0; }
  .nt-delete-btn:hover { background:var(--rose-dim); color:var(--rose); border-color:rgba(251,113,133,0.3); }

  /* Skeleton */
  .nt-skel { height:10px; border-radius:5px; background:linear-gradient(90deg,var(--bg-hover) 25%,rgba(99,102,241,0.05) 50%,var(--bg-hover) 75%); background-size:200% 100%; animation:nt-shimmer 1.8s infinite; }
  @keyframes nt-shimmer { 0%{ background-position:200% 0 } 100%{ background-position:-200% 0 } }

  /* Empty */
  .nt-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:48px; gap:8px; color:var(--text-muted); font-size:12px; }

  /* Pagination */
  .nt-pagination { display:flex; align-items:center; justify-content:space-between; padding:12px 20px; border-top:1px solid var(--border); }
  .nt-pag-info   { font-size:11px; color:var(--text-muted); font-family:var(--font-mono); }
  .nt-pag-pages  { display:flex; gap:4px; }
  .nt-pag-btn { width:28px; height:28px; border-radius:7px; display:flex; align-items:center; justify-content:center; font-size:11px; font-family:var(--font-mono); border:1px solid var(--border); background:none; color:var(--text-muted); cursor:pointer; transition:all 0.15s; }
  .nt-pag-btn:hover  { background:var(--bg-hover); color:var(--text-primary); }
  .nt-pag-btn.active { background:var(--accent-dim); color:var(--accent-light); border-color:rgba(99,102,241,0.3); }

  /* Error */
  .nt-error { display:flex; align-items:center; gap:8px; padding:10px 14px; border-radius:var(--radius-sm); background:rgba(251,113,133,0.08); border:1px solid rgba(251,113,133,0.22); color:var(--rose); font-size:12px; }

  /* Toast */
  .nt-toast { position:fixed; bottom:24px; right:24px; z-index:100; display:flex; align-items:center; gap:10px; padding:12px 18px; border-radius:var(--radius-md); background:var(--bg-elevated); border:1px solid var(--border-hi); box-shadow:0 8px 32px rgba(0,0,0,0.5); font-size:13px; color:var(--text-primary); animation:nt-slide-in 0.22s ease; }
  .nt-toast.ok  { border-color:rgba(52,211,153,0.35); }
  .nt-toast.err { border-color:rgba(251,113,133,0.35); }
  @keyframes nt-slide-in { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }

  /* Gate */
  .nt-gate { height:100vh; display:flex; align-items:center; justify-content:center; flex-direction:column; gap:14px; background:var(--bg-base); }
  .nt-gate-icon { width:36px; height:36px; border-radius:10px; background:linear-gradient(135deg,#6366f1,#a78bfa); box-shadow:0 0 24px rgba(99,102,241,0.4); display:flex; align-items:center; justify-content:center; }

  /* ── Mobile nav controls ──────────────────────────────────────────── */
  .ad-hamburger { display:none; width:32px; height:32px; border-radius:8px; border:1px solid var(--border-hi); background:var(--bg-card); color:var(--text-secondary); align-items:center; justify-content:center; cursor:pointer; flex-shrink:0; }
  .ad-hamburger:hover { color:var(--text-primary); }
  .ad-nav-close { display:none; margin-left:auto; width:28px; height:28px; border-radius:7px; border:none; background:transparent; color:var(--text-secondary); align-items:center; justify-content:center; cursor:pointer; flex-shrink:0; }
  .ad-backdrop { display:none; }

  /* ── Responsive breakpoints ───────────────────────────────────────── */
  @media (max-width: 1024px) {
    .ad-sidebar { position:fixed; top:0; left:0; bottom:0; transform:translateX(-100%); transition:transform 0.22s ease; z-index:60; box-shadow:0 0 40px rgba(0,0,0,0.5); }
    .ad-sidebar.open { transform:translateX(0); }
    .ad-hamburger { display:flex; }
    .ad-nav-close { display:flex; }
    .ad-backdrop { display:block; position:fixed; inset:0; background:rgba(0,0,0,0.55); backdrop-filter:blur(2px); z-index:55; }
    .nt-layout { grid-template-columns: 1fr; }
  }

  @media (max-width: 860px) {
    .nt-main { padding:18px 16px; gap:16px; }
    .nt-topbar { padding:0 14px; flex-wrap:wrap; height:auto; min-height:var(--topbar-h); gap:8px 0; padding-top:10px; padding-bottom:10px; }
    .nt-topbar-sub { display:none; }
    .nt-btn span { display:none; }
    .nt-toolbar { flex-direction:column; align-items:stretch; }
    .nt-toolbar-right { flex-wrap:wrap; width:100%; }
    .nt-search-wrap { flex:1; min-width:140px; }
    .nt-search { width:100%; }
    .nt-row { grid-template-columns:1fr; }
  }

  @media (max-width: 560px) {
    .nt-topbar-title { font-size:13.5px; }
    .nt-stat { padding:14px 14px; gap:8px; }
    .nt-stat-value { font-size:18px; }
    .nt-target-row { flex-direction:column; }
    .nt-pagination { flex-direction:column; gap:8px; align-items:flex-start; }
    .nt-row-meta { flex-wrap:wrap; }
  }
`;

function StyleInjector() {
  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = STYLES;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);
  return null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtDateTime(str) {
  if (!str) return "—";
  return new Date(str).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function initials(first, last) {
  return `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase() || "?";
}
const PALETTE = ["indigo", "green", "amber", "rose", "purple"];
function avatarColor(id) {
  const n = typeof id === "string" ? id.charCodeAt(0) + (id.charCodeAt(id.length - 1) || 0) : (id ?? 0);
  return PALETTE[n % PALETTE.length];
}

// ─── Sidebar link ─────────────────────────────────────────────────────────────
function SidebarLink({ to, label, icon: Icon, end, onClick }) {
  return (
    <NavLink to={to} end={end} onClick={onClick} className={({ isActive }) => `ad-nav-link${isActive ? " active" : ""}`}>
      {({ isActive }) => (
        <>
          <Icon size={14} />
          <span style={{ flex: 1 }}>{label}</span>
          {isActive && <ChevronRight size={10} className="ad-nav-chevron" />}
        </>
      )}
    </NavLink>
  );
}

// ─── Category chip ────────────────────────────────────────────────────────────
function CatChip({ category }) {
  return <span className={`nt-chip nt-chip-${category ?? "general"}`}>{category ?? "general"}</span>;
}

// ─── Notification icon ────────────────────────────────────────────────────────
function NotifIcon({ n }) {
  const bg    = n.icon_bg_css    || "rgba(99,102,241,0.15)";
  const color = n.icon_color_css || "#818cf8";
  return (
    <div className="nt-notif-icon" style={{ background: bg }}>
      <Bell size={14} style={{ color }} />
    </div>
  );
}

// ─── Tags input component ─────────────────────────────────────────────────────
function TagsInput({ tags, onChange }) {
  const [input, setInput] = useState("");

  function addTag(val) {
    const t = val.trim().toLowerCase();
    if (!t) return;
    // Normalise existing tags to strings before comparing
    const existing = tags.map((x) => typeof x === "string" ? x : (x?.label ?? x?.cls ?? ""));
    if (!existing.includes(t)) onChange([...tags, t]);
    setInput("");
  }

  function handleKey(e) {
    if (["Enter", ",", " "].includes(e.key)) {
      e.preventDefault();
      addTag(input);
    } else if (e.key === "Backspace" && !input && tags.length) {
      onChange(tags.slice(0, -1));
    }
  }

  return (
    <div className="nt-tags-wrap" onClick={() => document.getElementById("nt-tag-input-el")?.focus()}>
      {tags.map((t, idx) => {
        const label = typeof t === "string" ? t : (t?.label ?? t?.cls ?? String(t));
        return (
          <span key={`${label}-${idx}`} className="nt-tag">
            {label}
            <button className="nt-tag-remove" onClick={() => onChange(tags.filter((_, i) => i !== idx))}><X size={10} /></button>
          </span>
        );
      })}
      <input
        id="nt-tag-input-el"
        className="nt-tag-input"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKey}
        onBlur={() => input && addTag(input)}
        placeholder={tags.length ? "" : "Add tags (press Enter)…"}
      />
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AdminNotifications() {
  const navigate = useNavigate();

  const [adminEmail,    setAdminEmail]    = useState("");
  const [adminInitials, setAdminInitials] = useState("A");
  const [sessionReady,  setSessionReady]  = useState(false);
  const [navOpen,       setNavOpen]       = useState(false);

  // data
  const [history,  setHistory]  = useState([]);
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(true);

  // list controls
  const [tab,          setTab]         = useState("compose");
  const [search,       setSearch]      = useState("");
  const [catFilter,    setCatFilter]   = useState("");
  const [readFilter,   setReadFilter]  = useState("");
  const [sortDir,      setSortDir]     = useState(-1);
  const [page,         setPage]        = useState(1);
  const [deleting,     setDeleting]    = useState(null);

  // compose form
  const [target,      setTarget]      = useState("all");
  const [userId,      setUserId]      = useState("");
  const [userSearch,  setUserSearch]  = useState("");
  const [category,    setCategory]    = useState("general");
  const [title,       setTitle]       = useState("");
  const [description, setDescription] = useState("");
  const [actionUrl,   setActionUrl]   = useState("");
  const [tags,        setTags]        = useState([]);
  const [sending,     setSending]     = useState(false);
  const [formError,   setFormError]   = useState("");

  const [toast, setToast] = useState({ msg: "", ok: true });

  // ── Session gate ───────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) { navigate("/admin/login", { replace: true }); return; }
      const email = session.user?.email ?? "";
      setAdminEmail(email);
      const parts = email.split("@")[0].split(".");
      setAdminInitials((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? parts[0]?.[1] ?? ""));
      setSessionReady(true);
    });
  }, [navigate]);

  async function handleLogout() {
    await supabase.auth.signOut();
    navigate("/admin/login", { replace: true });
  }

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch notifications — only confirmed schema columns
      const { data: notifRows, error: notifErr } = await supabase
        .from("notifications")
        .select("id, user_id, category, title, description, icon_bg_css, icon_color_css, dot_color_css, is_read, is_dismissed, tags, action_url, created_at")
        .order("created_at", { ascending: false });
      if (notifErr) throw notifErr;

      // Fetch users separately, merge in
      const { data: userRows } = await supabase
        .from("users")
        .select("id, first_name, last_name, email")
        .order("first_name");

      const userMap = {};
      (userRows ?? []).forEach((u) => { userMap[u.id] = u; });

      setHistory((notifRows ?? []).map((n) => ({ ...n, users: userMap[n.user_id] ?? null })));
      setUsers(userRows ?? []);
    } catch (e) {
      console.error("[Notifications] fetch error:", e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (sessionReady) fetchAll(); }, [sessionReady, fetchAll]);

  function showToast(msg, ok = true) {
    setToast({ msg, ok });
    setTimeout(() => setToast({ msg: "", ok: true }), 3200);
  }

  // ── Send ───────────────────────────────────────────────────────────────────
  async function handleSend() {
    setFormError("");
    if (!title.trim()) { setFormError("Title is required."); return; }
    if (target === "user" && !userId) { setFormError("Select a user to send to."); return; }
    setSending(true);
    try {
      const base = {
        category,
        title: title.trim(),
        description: description.trim() || null,
        action_url: actionUrl.trim() || null,
        tags: tags.length ? tags : [],
        is_read: false,
        is_dismissed: false,
        created_at: new Date().toISOString(),
      };

      if (target === "all") {
        const rows = users.map((u) => ({ ...base, user_id: u.id }));
        const { error } = await supabase.from("notifications").insert(rows);
        if (error) throw error;
        showToast(`Sent to ${users.length} users.`);
      } else {
        const { error } = await supabase.from("notifications").insert({ ...base, user_id: userId });
        if (error) throw error;
        showToast("Notification sent.");
      }

      // Reset form
      setTitle(""); setDescription(""); setActionUrl(""); setTags([]);
      setCategory("general"); setUserId(""); setUserSearch(""); setTarget("all");
      await fetchAll();
      setTab("history");
    } catch (e) {
      setFormError(e.message ?? "Failed to send.");
    } finally {
      setSending(false);
    }
  }

  // ── Delete ─────────────────────────────────────────────────────────────────
  async function handleDelete(id) {
    setDeleting(id);
    try {
      const { error } = await supabase.from("notifications").delete().eq("id", id);
      if (error) throw error;
      setHistory((prev) => prev.filter((n) => n.id !== id));
      showToast("Notification deleted.");
    } catch (e) {
      showToast(e.message ?? "Failed to delete.", false);
    } finally {
      setDeleting(null);
    }
  }

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:     history.length,
    unread:    history.filter((n) => !n.is_read).length,
    dismissed: history.filter((n) => n.is_dismissed).length,
    users:     users.length,
  }), [history, users]);

  const STAT_CARDS = [
    { label: "Total sent",    value: stats.total,     icon: Bell,    bg: "rgba(99,102,241,0.15)",  color: "var(--accent-light)", sg: "rgba(99,102,241,0.08)",  sb: "var(--accent)"  },
    { label: "Unread",        value: stats.unread,    icon: EyeOff,  bg: "rgba(251,191,36,0.15)",  color: "var(--amber)",        sg: "rgba(251,191,36,0.08)",  sb: "var(--amber)"   },
    { label: "Dismissed",     value: stats.dismissed, icon: Eye,     bg: "rgba(52,211,153,0.15)",  color: "var(--green)",        sg: "rgba(52,211,153,0.08)",  sb: "var(--green)"   },
    { label: "Total users",   value: stats.users,     icon: Users,   bg: "rgba(167,139,250,0.15)", color: "var(--purple)",       sg: "rgba(167,139,250,0.08)", sb: "var(--purple)"  },
  ];

  // ── Filter list ────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return history
      .filter((n) => {
        const matchQ   = !q || n.title?.toLowerCase().includes(q) || n.description?.toLowerCase().includes(q) || `${n.users?.first_name ?? ""} ${n.users?.last_name ?? ""}`.toLowerCase().includes(q);
        const matchCat = !catFilter  || n.category === catFilter;
        const matchRead = !readFilter || (readFilter === "unread" ? !n.is_read : n.is_read);
        return matchQ && matchCat && matchRead;
      })
      .sort((a, b) => (new Date(a.created_at) - new Date(b.created_at)) * sortDir);
  }, [history, search, catFilter, readFilter, sortDir]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const pageSlice  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // ── Filtered user list in picker ───────────────────────────────────────────
  const filteredUsers = useMemo(() => {
    const q = userSearch.toLowerCase();
    return !q ? users : users.filter((u) =>
      `${u.first_name} ${u.last_name}`.toLowerCase().includes(q) ||
      (u.email ?? "").toLowerCase().includes(q)
    );
  }, [users, userSearch]);

  if (!sessionReady) {
    return (
      <div className="nt-gate">
        <StyleInjector />
        <div className="nt-gate-icon"><Bell size={16} color="#fff" /></div>
        <p style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "Inter, sans-serif" }}>Verifying session…</p>
      </div>
    );
  }

  return (
    <div className="nt-shell">
      <StyleInjector />

      {/* ── Mobile backdrop ── */}
      {navOpen && <div className="ad-backdrop" onClick={() => setNavOpen(false)} />}

      {/* ── Sidebar ── */}
      <aside className={`ad-sidebar${navOpen ? " open" : ""}`}>
        <div className="ad-logo">
          <div className="ad-logo-icon"><CandlestickChart size={16} color="#fff" /></div>
          <div className="ad-logo-text">
            <strong>Tradexflow</strong>
            <span>Admin panel</span>
          </div>
          <button className="ad-nav-close" onClick={() => setNavOpen(false)}><X size={16} /></button>
        </div>

        <nav className="ad-nav">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label}>
              <p className="ad-nav-section-label">{section.label}</p>
              <div className="ad-nav-items">
                {section.items.map((item) => <SidebarLink key={item.to} {...item} onClick={() => setNavOpen(false)} />)}
              </div>
            </div>
          ))}
        </nav>

        <div className="ad-user-card">
          <div className="ad-user-inner">
            <div className="ad-user-avatar">{adminInitials.toUpperCase()}</div>
            <div className="ad-user-info">
              <strong>Super Admin</strong>
              <span>{adminEmail}</span>
            </div>
            <button className="ad-logout-btn" onClick={handleLogout} title="Logout">
              <LogOut size={13} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Page ── */}
      <div className="nt-page">

        {/* Topbar */}
        <header className="nt-topbar">
          <div className="nt-topbar-left">
            <button className="ad-hamburger" onClick={() => setNavOpen(true)}><Menu size={18} /></button>
            <div className="nt-topbar-badge"><Bell size={9} />Notifications</div>
            <div>
              <div className="nt-topbar-title">Notification center</div>
              <div className="nt-topbar-sub">Compose and broadcast messages to users</div>
            </div>
          </div>
          <div className="nt-topbar-right">
            <button className="nt-btn" onClick={fetchAll}><RefreshCw size={12} /><span>Refresh</span></button>
          </div>
        </header>

        <main className="nt-main">

          {/* Stats */}
          <div className="nt-stat-grid">
            {STAT_CARDS.map(({ label, value, icon: Icon, bg, color, sg, sb }) => (
              <div key={label} className="nt-stat" style={{ "--sg": sg, "--sb": sb }}>
                <div className="nt-stat-top">
                  <span className="nt-stat-label">{label}</span>
                  <div className="nt-stat-icon" style={{ background: bg }}><Icon size={14} style={{ color }} /></div>
                </div>
                <div className="nt-stat-value">{value}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="nt-tabs">
            {[
              { key: "compose", label: "Compose",     badge: null },
              { key: "history", label: "History",     badge: history.length },
            ].map(({ key, label, badge }) => (
              <button key={key} className={`nt-tab${tab === key ? " active" : ""}`} onClick={() => setTab(key)}>
                {label}
                {badge != null && (
                  <span className={`nt-tab-badge${key === "history" && stats.unread > 0 ? " unread" : ""}`}>
                    {key === "history" ? stats.unread > 0 ? stats.unread : badge : badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* ── COMPOSE ─────────────────────────────────────────────────────── */}
          {tab === "compose" && (
            <div className="nt-layout">

              {/* Form panel */}
              <div className="nt-panel">
                <div className="nt-panel-head">
                  <div className="nt-panel-title">New notification</div>
                  <div className="nt-panel-sub">Fill in the details below and send</div>
                </div>

                <div className="nt-form">

                  {/* Target */}
                  <div>
                    <label className="nt-label">Send to</label>
                    <div className="nt-target-row">
                      {[
                        { val: "all",  label: `All users (${users.length})`, icon: Users },
                        { val: "user", label: "Specific user",               icon: User  },
                      ].map(({ val, label, icon: Icon }) => (
                        <button key={val}
                          className={`nt-target-btn${target === val ? " active" : ""}`}
                          onClick={() => { setTarget(val); setUserId(""); setUserSearch(""); }}
                        >
                          <Icon size={13} />{label}
                        </button>
                      ))}
                    </div>

                    {/* User picker */}
                    {target === "user" && (
                      <div style={{ marginTop: 10 }}>
                        <div className="nt-search-wrap">
                          <Search size={12} className="nt-search-icon" />
                          <input
                            type="text" className="nt-search" style={{ width: "100%" }}
                            placeholder="Search by name or email…"
                            value={userSearch} onChange={(e) => setUserSearch(e.target.value)}
                          />
                        </div>
                        <div className="nt-user-list">
                          {filteredUsers.slice(0, 30).map((u) => {
                            const aColor = avatarColor(u.id);
                            return (
                              <div
                                key={u.id}
                                className={`nt-user-item${userId === u.id ? " selected" : ""}`}
                                onClick={() => { setUserId(u.id); setUserSearch(`${u.first_name} ${u.last_name}`); }}
                              >
                                <div className={`nt-avatar nt-avatar-${aColor}`}>{initials(u.first_name, u.last_name)}</div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 12.5, fontWeight: 500, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.first_name} {u.last_name}</div>
                                  <div style={{ fontSize: 10.5, color: "var(--text-muted)" }}>{u.email}</div>
                                </div>
                                {userId === u.id && <CheckCircle2 size={13} style={{ color: "var(--accent-light)", flexShrink: 0 }} />}
                              </div>
                            );
                          })}
                          {filteredUsers.length === 0 && (
                            <div style={{ padding: "16px", textAlign: "center", fontSize: 12, color: "var(--text-muted)" }}>No users found</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Category */}
                  <div>
                    <label className="nt-label">Category</label>
                    <div className="nt-cat-pills">
                      {CATEGORIES.map((c) => (
                        <button key={c.value}
                          className={`nt-cat-pill${category === c.value ? " active" : ""}`}
                          onClick={() => setCategory(c.value)}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="nt-label">Title <span style={{ color: "var(--rose)" }}>*</span></label>
                    <input type="text" className="nt-input" placeholder="Notification title…"
                      value={title} onChange={(e) => setTitle(e.target.value)} />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="nt-label">Description <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(optional)</span></label>
                    <textarea className="nt-textarea" rows={3} placeholder="Notification body text…"
                      value={description} onChange={(e) => setDescription(e.target.value)} />
                  </div>

                  {/* Tags */}
                  <div>
                    <label className="nt-label" style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <Tag size={11} />Tags <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(optional)</span>
                    </label>
                    <TagsInput tags={tags} onChange={setTags} />
                  </div>

                  {/* Action URL */}
                  <div>
                    <label className="nt-label" style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <Link size={11} />Action URL <span style={{ color: "var(--text-muted)", fontWeight: 400 }}>(optional)</span>
                    </label>
                    <input type="url" className="nt-input" placeholder="https://…"
                      value={actionUrl} onChange={(e) => setActionUrl(e.target.value)} />
                  </div>

                  {formError && (
                    <div className="nt-error"><AlertCircle size={13} style={{ flexShrink: 0 }} />{formError}</div>
                  )}

                  <button className="nt-btn-primary" style={{ width: "100%", justifyContent: "center", padding: "11px" }}
                    onClick={handleSend} disabled={sending}>
                    <Send size={13} />
                    {sending ? "Sending…" : target === "all" ? `Broadcast to all ${users.length} users` : "Send to user"}
                  </button>
                </div>
              </div>

              {/* Live preview panel */}
              <div className="nt-panel" style={{ position: "sticky", top: 0 }}>
                <div className="nt-panel-head">
                  <div className="nt-panel-title">Preview</div>
                  <div className="nt-panel-sub">How this notification will appear</div>
                </div>
                <div style={{ padding: 20 }}>
                  <div style={{ padding: "14px 16px", borderRadius: "var(--radius-md)", background: "var(--bg-elevated)", border: "1px solid var(--border)", display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(99,102,241,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Bell size={15} style={{ color: "var(--accent-light)" }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: title ? "var(--text-primary)" : "var(--text-muted)" }}>
                          {title || "Notification title…"}
                        </span>
                        <CatChip category={category} />
                      </div>
                      <p style={{ fontSize: 12, color: description ? "var(--text-secondary)" : "var(--text-muted)", lineHeight: 1.5 }}>
                        {description || "Your notification description will appear here."}
                      </p>
                      {tags.length > 0 && (
                        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 8 }}>
                          {tags.map((t, i) => {
                            const label = typeof t === "string" ? t : (t?.label ?? t?.cls ?? JSON.stringify(t));
                            return <span key={`${label}-${i}`} className="nt-tag">{label}</span>;
                          })}
                        </div>
                      )}
                      {actionUrl && (
                        <div style={{ marginTop: 8, fontSize: 11, color: "var(--accent-light)", textDecoration: "underline", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {actionUrl}
                        </div>
                      )}
                      <div style={{ fontSize: 10.5, color: "var(--text-muted)", marginTop: 8, fontFamily: "var(--font-mono)" }}>
                        Just now · {target === "all" ? `${users.length} users` : "1 user"}
                      </div>
                    </div>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--accent)", flexShrink: 0, marginTop: 6 }} />
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* ── HISTORY ─────────────────────────────────────────────────────── */}
          {tab === "history" && (
            <div className="nt-panel">
              {/* Toolbar */}
              <div className="nt-toolbar">
                <div className="nt-toolbar-left">
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>Sent notifications</span>
                  <span className="nt-count-pill">{filtered.length}</span>
                </div>
                <div className="nt-toolbar-right">
                  <Filter size={12} style={{ color: "var(--text-muted)" }} />
                  <select className="nt-filter-select" value={catFilter} onChange={(e) => { setCatFilter(e.target.value); setPage(1); }}>
                    <option value="">All categories</option>
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                  <select className="nt-filter-select" value={readFilter} onChange={(e) => { setReadFilter(e.target.value); setPage(1); }}>
                    <option value="">All</option>
                    <option value="unread">Unread</option>
                    <option value="read">Read</option>
                  </select>
                  <button className="nt-btn" onClick={() => setSortDir((d) => d * -1)} style={{ padding: "5px 10px" }}>
                    {sortDir === -1 ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
                    Date
                  </button>
                  <div className="nt-search-wrap">
                    <Search size={12} className="nt-search-icon" />
                    <input type="text" className="nt-search" placeholder="Search notifications…"
                      value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
                  </div>
                </div>
              </div>

              {/* Rows */}
              {loading ? (
                <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: 14 }}>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                      <div style={{ width: 34, height: 34, borderRadius: 9, background: "var(--bg-hover)", flexShrink: 0 }} />
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
                        <div className="nt-skel" style={{ width: `${40 + i * 6}%` }} />
                        <div className="nt-skel" style={{ width: `${55 + i * 4}%` }} />
                        <div className="nt-skel" style={{ width: "25%" }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : pageSlice.length === 0 ? (
                <div className="nt-empty"><Bell size={24} /><span>No notifications match your filters</span></div>
              ) : (
                pageSlice.map((n) => (
                  <div key={n.id} className="nt-row-item">
                    <NotifIcon n={n} />
                    <div className="nt-row-body">
                      <div className="nt-row-top">
                        <span className="nt-row-title">{n.title}</span>
                        <CatChip category={n.category} />
                        {!n.is_read && <span className="nt-unread-dot" />}
                        {n.is_dismissed && <span style={{ fontSize: 10, color: "var(--text-muted)", fontStyle: "italic" }}>dismissed</span>}
                        {Array.isArray(n.tags) && n.tags.length > 0 && (
                          <div style={{ display: "flex", gap: 4 }}>
                            {n.tags.slice(0, 3).map((t, i) => {
                              const label = typeof t === "string" ? t : (t?.label ?? t?.cls ?? JSON.stringify(t));
                              return <span key={`${label}-${i}`} className="nt-tag">{label}</span>;
                            })}
                            {n.tags.length > 3 && <span style={{ fontSize: 10, color: "var(--text-muted)" }}>+{n.tags.length - 3}</span>}
                          </div>
                        )}
                      </div>
                      {n.description && <div className="nt-row-desc">{n.description}</div>}
                      <div className="nt-row-meta">
                        <span className="nt-row-date">{fmtDateTime(n.created_at)}</span>
                        <span className="nt-row-to">
                          → {n.users
                            ? <strong>{n.users.first_name} {n.users.last_name}</strong>
                            : <strong style={{ color: "var(--purple)" }}>All users</strong>}
                        </span>
                        {n.action_url && (
                          <a href={n.action_url} target="_blank" rel="noreferrer"
                            style={{ fontSize: 10.5, color: "var(--accent-light)", textDecoration: "underline" }}>
                            Link
                          </a>
                        )}
                      </div>
                    </div>
                    <button
                      className="nt-delete-btn"
                      onClick={() => handleDelete(n.id)}
                      disabled={deleting === n.id}
                      title="Delete"
                    >
                      {deleting === n.id
                        ? <RefreshCw size={11} style={{ animation: "nt-shimmer 1s linear infinite" }} />
                        : <Trash2 size={11} />}
                    </button>
                  </div>
                ))
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="nt-pagination">
                  <span className="nt-pag-info">
                    {filtered.length
                      ? `${(page - 1) * PER_PAGE + 1}–${Math.min(page * PER_PAGE, filtered.length)} of ${filtered.length}`
                      : "0 results"}
                  </span>
                  <div className="nt-pag-pages">
                    {Array.from({ length: Math.min(totalPages, 8) }, (_, i) => i + 1).map((p) => (
                      <button key={p} className={`nt-pag-btn${page === p ? " active" : ""}`} onClick={() => setPage(p)}>{p}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

        </main>
      </div>

      {/* Toast */}
      {toast.msg && (
        <div className={`nt-toast ${toast.ok ? "ok" : "err"}`}>
          {toast.ok
            ? <CheckCircle2 size={14} style={{ color: "var(--green)", flexShrink: 0 }} />
            : <XCircle      size={14} style={{ color: "var(--rose)",  flexShrink: 0 }} />}
          {toast.msg}
        </div>
      )}
    </div>
  );
}